import { DateRange } from '@mui/x-date-pickers-pro'

export const startOfDay = (date: Date): Date => {
  const result = new Date(date)
  result.setHours(0, 0, 0, 0)
  return result
}

export const endOfDay = (date: Date): Date => {
  const result = new Date(date)
  result.setHours(23, 59, 59, 999)
  return result
}

export const getYearRange = (year: number): DateRange<Date> => {
  return [startOfDay(new Date(year, 0, 1)), endOfDay(new Date(year, 11, 31))]
}

export const isCurrentYear = (year: number, now = new Date()): boolean => {
  return year === now.getFullYear()
}

export const getActiveYearRange = (year: number, now = new Date()): DateRange<Date> => {
  if (isCurrentYear(year, now)) {
    return [startOfDay(new Date(year, 0, 1)), endOfDay(now)]
  }

  return getYearRange(year)
}

export const getYearDiscoveryRange = (year: number, now = new Date()): DateRange<Date> => {
  return getActiveYearRange(year, now)
}

export const normalizeDateRange = (range: DateRange<Date>): DateRange<Date> | null => {
  const [start, end] = range
  if (!start || !end) {
    return null
  }

  return [startOfDay(start), endOfDay(end)]
}

export const TIMELINE_LABELS = ['today', 'currentMonth', 'lastMonth', 'all'] as const
export type TimelineLabel = typeof TIMELINE_LABELS[number]

export type TimelineValue = TimelineLabel | `year-${number}`

export const isYearTimeline = (value: TimelineValue): value is `year-${number}` => {
  return value.startsWith('year-')
}

export const parseYearTimeline = (value: TimelineValue): number | null => {
  if (!isYearTimeline(value)) {
    return null
  }

  return Number(value.slice(5))
}

const getSameDayInMonth = (year: number, month: number, day: number): Date => {
  const lastDayOfMonth = new Date(year, month + 1, 0).getDate()
  return new Date(year, month, Math.min(day, lastDayOfMonth))
}

export const supportsTrendComparison = (value: TimelineValue): boolean => {
  return value === 'currentMonth' || value === 'lastMonth' || isYearTimeline(value)
}

export type ComparisonDirection = 'backward' | 'forward'

export const getComparisonPeriod = (
  value: TimelineValue,
  direction: ComparisonDirection = 'backward',
  now = new Date()
): { current: DateRange<Date>; comparison: DateRange<Date> } | null => {
  if (!supportsTrendComparison(value)) {
    return null
  }

  const offset = direction === 'backward' ? -1 : 1

  const year = parseYearTimeline(value)
  if (year !== null) {
    const comparisonYear = year + offset

    if (isCurrentYear(year, now)) {
      // Incomplete year: compare the same Jan 1 – today slice of the other year.
      const currentEnd = endOfDay(now)
      const currentStart = startOfDay(new Date(year, 0, 1))
      const comparisonStart = startOfDay(new Date(comparisonYear, 0, 1))
      const comparisonEnd = endOfDay(getSameDayInMonth(comparisonYear, now.getMonth(), now.getDate()))

      return {
        current: [currentStart, currentEnd],
        comparison: [comparisonStart, comparisonEnd]
      }
    }

    return {
      current: getYearRange(year),
      comparison: getYearRange(comparisonYear)
    }
  }

  switch (value) {
    case 'currentMonth': {
      const currentStart = startOfDay(new Date(now.getFullYear(), now.getMonth(), 1))
      const currentEnd = endOfDay(now)
      const comparisonStart = startOfDay(new Date(now.getFullYear(), now.getMonth() + offset, 1))
      const comparisonEnd = endOfDay(getSameDayInMonth(now.getFullYear(), now.getMonth() + offset, now.getDate()))

      return {
        current: [currentStart, currentEnd],
        comparison: [comparisonStart, comparisonEnd]
      }
    }
    case 'lastMonth': {
      const currentStart = startOfDay(new Date(now.getFullYear(), now.getMonth() - 1, 1))
      const currentEnd = endOfDay(new Date(now.getFullYear(), now.getMonth(), 0))
      const comparisonStart = startOfDay(new Date(now.getFullYear(), now.getMonth() - 1 + offset, 1))
      const comparisonEnd = endOfDay(new Date(now.getFullYear(), now.getMonth() + offset, 0))

      return {
        current: [currentStart, currentEnd],
        comparison: [comparisonStart, comparisonEnd]
      }
    }
    default:
      return null
  }
}

const addDays = (date: Date, days: number): Date =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate() + days)

export const getCustomComparisonPeriod = (
  range: DateRange<Date>,
  direction: ComparisonDirection = 'backward'
): { current: DateRange<Date>; comparison: DateRange<Date> } | null => {
  const normalized = normalizeDateRange(range)
  if (!normalized) {
    return null
  }

  const [start, end] = normalized
  if (!start || !end) {
    return null
  }

  const dayMs = 24 * 60 * 60 * 1000
  // Inclusive length of the selected window in days.
  const lengthDays =
    Math.round((startOfDay(end).getTime() - startOfDay(start).getTime()) / dayMs) + 1

  if (direction === 'forward') {
    // Same-length window immediately after the selection (starts the day after it ends).
    const comparisonStart = startOfDay(addDays(end, 1))
    const comparisonEnd = endOfDay(addDays(end, lengthDays))

    return {
      current: [start, end],
      comparison: [comparisonStart, comparisonEnd]
    }
  }

  // Same-length window immediately before the selection (ends the day before it starts).
  const comparisonEnd = endOfDay(addDays(start, -1))
  const comparisonStart = startOfDay(addDays(start, -lengthDays))

  return {
    current: [start, end],
    comparison: [comparisonStart, comparisonEnd]
  }
}

export type Trend =
  | { type: 'percentage'; value: number }
  | { type: 'new'; increase: number }

export const calculateTrend = (current: number, previous: number): Trend => {
  if (previous === 0) {
    // No baseline for a ratio: report the fresh gain instead of a misleading +100%.
    return current === 0
      ? { type: 'percentage', value: 0 }
      : { type: 'new', increase: current }
  }

  return { type: 'percentage', value: ((current - previous) / previous) * 100 }
}

export const getTimelineRange = (value: TimelineValue, now = new Date()): DateRange<Date> => {
  const year = parseYearTimeline(value)
  if (year !== null) {
    return getActiveYearRange(year, now)
  }

  switch (value) {
    case 'today':
      return [startOfDay(now), endOfDay(now)]
    case 'currentMonth':
      return [startOfDay(new Date(now.getFullYear(), now.getMonth(), 1)), endOfDay(now)]
    case 'lastMonth':
      return [
        startOfDay(new Date(now.getFullYear(), now.getMonth() - 1, 1)),
        endOfDay(new Date(now.getFullYear(), now.getMonth(), 0))
      ]
    case 'all':
      return [startOfDay(new Date(1970, 0, 1)), endOfDay(now)]
    default:
      return [startOfDay(now), endOfDay(now)]
  }
}
